import type { SiteSectionCategoryId, V4HSiteId } from "@/data/common/ids";
import type { SectionCategory, UpdateSectionCategoryRequest } from "@/domains/v4b/site-section/site-section.types";
import type { Ref } from "vue";

import { normalizeForSearch } from "@voltalis/frontend-utils";
import { computed } from "vue";

import { useDeleteSectionCategoryMutation } from "@/composables/kili/sections/mutations/use-delete-section-category-mutation";
import { useUpdateSectionCategoryMutation } from "@/composables/kili/sections/mutations/use-update-section-category-mutation";
import { useGetSiteSectionCategoriesQuery } from "@/composables/kili/sections/queries/use-get-site-section-categories-query";
import { useSiteSectionCategory } from "@/composables/kili/sections/use-site-section-category";
import { useToaster } from "@/composables/layout/use-toaster";
import { EV4bErrorCode } from "@/data/api/kili/common/kili-errors.type";
import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

type ToastSeverity = "success" | "error";

interface CategoryMutationMessages {
  success: string;
  errorFallbackKey: string;
}

interface DeleteCategoryMessages {
  success: string;
  error: string;
}

interface CategoryToast {
  severity: ToastSeverity;
  detail: string;
}

interface SiteSectionCategoryOption {
  id: SiteSectionCategoryId;
  label: string;
}

interface UpdateSiteSectionCategoryWithFeedbackArgs {
  categoryId: SiteSectionCategoryId;
  name: string;
  updateCategory: (command: UpdateSectionCategoryRequest & { id: SiteSectionCategoryId }) => Promise<unknown>;
  toastAdd: (opts: CategoryToast) => void;
  messages: CategoryMutationMessages;
}

interface DeleteSiteSectionCategoryWithFeedbackArgs {
  categoryId: SiteSectionCategoryId;
  deleteCategory: (categoryId: SiteSectionCategoryId) => Promise<unknown>;
  toastAdd: (opts: CategoryToast) => void;
  messages: DeleteCategoryMessages;
}

export async function updateSiteSectionCategoryWithFeedback({
  categoryId,
  name,
  updateCategory,
  toastAdd,
  messages,
}: UpdateSiteSectionCategoryWithFeedbackArgs): Promise<boolean> {
  try {
    await updateCategory({ id: categoryId, name });

    toastAdd({
      severity: "success",
      detail: messages.success,
    });

    MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_RENAMED, {
      category_id: categoryId,
    });

    return true;
  } catch (error) {
    toastKiliError(error, messages.errorFallbackKey);
    return false;
  }
}

export async function deleteSiteSectionCategoryWithFeedback({
  categoryId,
  deleteCategory,
  toastAdd,
  messages,
}: DeleteSiteSectionCategoryWithFeedbackArgs): Promise<boolean> {
  try {
    await deleteCategory(categoryId);

    toastAdd({
      severity: "success",
      detail: messages.success,
    });

    MixpanelService.trackEvent(EMixpanelEvent.SECTION_CATEGORY_DELETED, {
      category_id: categoryId,
    });

    return true;
  } catch {
    toastAdd({
      severity: "error",
      detail: messages.error,
    });

    return false;
  }
}

export function useManageSiteSectionCategories(
  siteId: Ref<V4HSiteId>,
  selectedSectionCategories?: Ref<SiteSectionCategoryId[]>,
) {
  const { t, k } = useTypedIntl();
  const toast = useToaster();

  const { data: sectionCategories, isLoading: isFetchingSectionCategories } = useGetSiteSectionCategoriesQuery(siteId);
  const { createSiteSectionCategory, isCreatingSectionCategory } = useSiteSectionCategory(siteId);
  const { mutateAsync: updateCategory, isPending: isUpdatingSectionCategory } =
    useUpdateSectionCategoryMutation(siteId);
  const { mutateAsync: deleteCategory, isPending: isDeletingSectionCategory } =
    useDeleteSectionCategoryMutation(siteId);

  const sectionCategoryFilterOptions = computed<SiteSectionCategoryOption[]>(() => {
    return (sectionCategories.value ?? []).map((category) => ({
      id: category.id,
      label: category.name,
    }));
  });

  function findSectionCategoryByName(
    name: string,
    ignoredCategoryId?: SiteSectionCategoryId,
  ): SectionCategory | undefined {
    const normalizedCategoryName = normalizeForSearch(name);

    return (sectionCategories.value ?? []).find(
      (category) => category.id !== ignoredCategoryId && normalizeForSearch(category.name) === normalizedCategoryName,
    );
  }

  function toastDuplicatedSectionCategoryName() {
    toast.add({
      severity: "error",
      detail: t(k.error[EV4bErrorCode.SITE_SECTION_CATEGORY_NAME_EXISTS_ON_SITE]),
    });
  }

  async function createSectionCategory(name: string): Promise<boolean> {
    const categoryName = name.trim();
    if (!categoryName) return false;

    if (findSectionCategoryByName(categoryName)) {
      toastDuplicatedSectionCategoryName();
      return false;
    }

    return !!(await createSiteSectionCategory(categoryName));
  }

  function updateSectionCategory(categoryId: SiteSectionCategoryId, name: string): Promise<boolean> {
    const categoryName = name.trim();
    if (!categoryName) return Promise.resolve(false);

    const currentCategory = (sectionCategories.value ?? []).find((category) => category.id === categoryId);
    if (currentCategory && currentCategory.name.trim() === categoryName) {
      return Promise.resolve(true);
    }

    if (findSectionCategoryByName(categoryName, categoryId)) {
      toastDuplicatedSectionCategoryName();
      return Promise.resolve(false);
    }

    return updateSiteSectionCategoryWithFeedback({
      categoryId,
      name: categoryName,
      updateCategory,
      toastAdd: toast.add,
      messages: {
        success: t(k.tools.siteManagement.siteTreeStructure.categoryUpdate.message.success),
        errorFallbackKey: "tools.siteManagement.siteTreeStructure.categoryUpdate.message.error",
      },
    });
  }

  async function deleteSectionCategory(categoryId: SiteSectionCategoryId): Promise<boolean> {
    const wasDeleted = await deleteSiteSectionCategoryWithFeedback({
      categoryId,
      deleteCategory,
      toastAdd: toast.add,
      messages: {
        success: t(k.tools.siteManagement.siteTreeStructure.categoryDeletion.message.success),
        error: t(k.tools.siteManagement.siteTreeStructure.categoryDeletion.message.error),
      },
    });

    if (wasDeleted && selectedSectionCategories) {
      selectedSectionCategories.value = selectedSectionCategories.value.filter(
        (selectedCategoryId) => selectedCategoryId !== categoryId,
      );
    }

    return wasDeleted;
  }

  return {
    sectionCategories,
    sectionCategoryFilterOptions,
    isFetchingSectionCategories,
    isCreatingSectionCategory,
    isUpdatingSectionCategory,
    isDeletingSectionCategory,
    findSectionCategoryByName,
    createSectionCategory,
    updateSectionCategory,
    deleteSectionCategory,
  };
}
